/**
 * The celebration surface's busy predicate and once-idle wait, built on the overlay intent
 * channel (`lib/overlay/intents.ts`) and the grant mutex (`lib/overlay/mutex.ts`).
 *
 * `CelebrationHost` is the sole consumer of `celebration` intents — producers call
 * `postIntent({ surface: 'celebration', payload: { kind, source } })` directly, not through this
 * module. What lives here is what other overlays need in order to coordinate around a
 * celebration without knowing anything about intents or drawers: whether one is pending, queued
 * or on screen right now (`isCelebrationBusy`), a way to be told when that changes
 * (`onCelebrationBusyChange`), and a one-shot wait for it to go idle (`awaitCelebrationIdle`)
 * that the install and push prompts use before asking the mutex for their own turn.
 *
 * The same questions for every coordinated surface at once live beside the mutex, in
 * `lib/overlay/coordinatedSurfaces.ts`.
 *
 * The once-EVER guard ("has this celebration already shown") lives in onboardingProgress —
 * CelebrationHost checks/sets it via isCompleted/markCompleted against the
 * SignupCelebration/EventGoingCelebration flow ids, not here.
 */
import { awaitIdle, onCoordinatedSurfacesChange } from '@lib/overlay/coordinatedSurfaces';
import { getIntents } from '@lib/overlay/intents';
import { hasPendingOrActiveGrant } from '@lib/overlay/mutex';

/**
 * `registration_check` is the Going celebration that asks "have a ticket?" (TECH-575).
 * `event_pin` is the Going sheet that asks for the PIN of each PIN host Space (TECH-663).
 */
export type CelebrationKind = 'signup' | 'event_going' | 'registration_check' | 'event_pin';

/** Mutex-grant id prefix for celebration drawers — shared with CelebrationHost. */
export const CELEBRATION_GRANT_PREFIX = 'celebration:';

/**
 * The busy predicate (overlay-system-design §6.3, §7 pull model): a pending `celebration`
 * intent ∨ a celebration-prefixed pending acquire ∨ an active celebration grant.
 *
 * The intent term covers cold start and every queued-but-unclaimed celebration: `postIntent`
 * writes it before CelebrationHost exists to read it, and it stays true until CelebrationHost
 * acks it — when the grant arrives (acquire happened at claim), on a stale settle, or at once
 * when the once-ever guard suppresses it, so the intent term hands over to the mutex term with
 * no gap. The TTL prune is the backstop for any intent none of those paths ever reach.
 */
export function isCelebrationBusy(): boolean {
  return (
    getIntents().some(intent => intent.surface === 'celebration') ||
    hasPendingOrActiveGrant(CELEBRATION_GRANT_PREFIX)
  );
}

/**
 * Subscribes to every transition that can move `isCelebrationBusy`. Its two terms live in the intent
 * channel and the mutex, and `onCoordinatedSurfacesChange` already forwards both; a subscriber
 * re-reads the predicate on each notify rather than inspecting the notify itself. Returns
 * unsubscribe.
 */
export const onCelebrationBusyChange = onCoordinatedSurfacesChange;

/**
 * Resolves true once no celebration is pending, queued or on screen; false if
 * `signal` aborts first. Never rejects. A prompt awaits this before asking the
 * mutex for its turn, so it lands after the celebration rather than being
 * queued ahead of one whose intent hasn't been acked yet.
 *
 * A `true` can still reach a caller whose signal aborted after resolution;
 * check `signal.aborted` after the await.
 */
export function awaitCelebrationIdle(signal: AbortSignal): Promise<boolean> {
  return awaitIdle(isCelebrationBusy, onCelebrationBusyChange, signal);
}
